import { useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { pollWithWake } from './kioskRefresh';

// Shared client hook for the family Google Calendar. Both the kiosk's
// Bills widget (for the per-day event dots) and the UpcomingEventsWidget
// pull from /api/calendar via this hook. The server side caches for 15
// min, so multiple consumers don't hammer Google — the per-consumer
// REFRESH_MS just keeps the in-memory state fresh.

export type CalendarEventSource = 'family' | 'expirations' | 'holidays';

export interface CalendarEvent {
  id: string;
  title: string;
  /** ISO 8601 date-time for timed events, or YYYY-MM-DD for all-day. */
  start: string;
  end: string;
  allDay: boolean;
  source: CalendarEventSource;
}

interface CalendarResponse {
  events: CalendarEvent[];
  windowStart: string;
  windowEnd: string;
  updatedAt: string;
  stale?: boolean;
}

const REFRESH_MS = 15 * 60 * 1000;

export function useCalendarEvents(): {
  events: CalendarEvent[];
  stale: boolean;
  error: string | null;
} {
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const res = await apiFetch('/api/calendar');
        if (!alive) return;
        if (!res.ok) {
          setError(`calendar ${res.status}`);
          return;
        }
        const json = (await res.json()) as CalendarResponse;
        setData(json);
        setError(null);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'fetch failed');
      }
    }
    void tick();
    const stop = pollWithWake(tick, REFRESH_MS);
    return () => {
      alive = false;
      stop();
    };
  }, []);

  return {
    events: data?.events ?? [],
    stale: !!data?.stale,
    error,
  };
}
